import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { cn } from '@/lib/utils';

/**
 * The chat box, with mentions as real elements.
 *
 * It used to be a textarea with the badges painted on a layer behind it, the
 * textarea's own text made transparent. That works only while the painted
 * layer advances by EXACTLY what the hidden text advances by — so the badge
 * could never have a picture in it, or padding, or a margin. Every attempt
 * bought one of those at the cost of the caret drifting away from the words.
 *
 * Here the badge IS an element: `contenteditable="false"`, so the caret steps
 * over it as one object, backspace removes it whole, and it can carry an
 * avatar and whatever padding it likes. Nothing has to be kept in step with
 * anything, because there is only one layer.
 *
 * The DOM is the editor. React does not re-render its children while typing —
 * that would destroy the caret on every keystroke — so `value` is applied only
 * when it differs from what this component last reported.
 */

export interface MentionProduct {
    uuid: string;
    name: string;
    thumbnailPath?: string;
}

export interface MentionComposerHandle {
    focus: () => void;
    /** Write a chosen product in at the caret, replacing the `@query` being typed. */
    insertMention: (product: MentionProduct) => void;
}

/** What the editor currently says, as plain text the rest of the app understands. */
function readEditor(root: HTMLElement): string {
    let out = '';
    const walk = (node: Node) => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                out += child.nodeValue || '';
                return;
            }
            const el = child as HTMLElement;
            if (el.dataset?.mention) { out += `@${el.dataset.mention}`; return; }
            if (el.tagName === 'BR') { out += '\n'; return; }
            walk(el);
        });
    };
    walk(root);
    return out;
}

/** The text between the start of the box and the caret. */
function textBeforeCaret(root: HTMLElement): string {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return readEditor(root);
    const range = sel.getRangeAt(0).cloneRange();
    range.selectNodeContents(root);
    const end = sel.getRangeAt(0);
    range.setEnd(end.endContainer, end.endOffset);
    const fragment = range.cloneContents();
    const holder = document.createElement('div');
    holder.appendChild(fragment);
    return readEditor(holder);
}

export const MentionComposer = React.forwardRef<MentionComposerHandle, {
    value: string;
    disabled?: boolean;
    placeholder?: string;
    products: MentionProduct[];
    onChange: (value: string, caretText: string) => void;
    onSubmit: () => void;
    onKeyDown?: (e: React.KeyboardEvent) => boolean | void;
}>(({ value, disabled, placeholder, products, onChange, onSubmit, onKeyDown }, ref) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    /** What we last told the parent, so an echo of it never rebuilds the DOM. */
    const emitted = useRef<string>('');
    const productsRef = useRef(products);
    productsRef.current = products;

    /** Build a badge element for one product. */
    const badgeFor = useCallback((product: MentionProduct): HTMLElement => {
        const span = document.createElement('span');
        span.contentEditable = 'false';
        span.dataset.mention = product.name;
        span.className = 'cs-mention';
        if (product.thumbnailPath) {
            const img = document.createElement('img');
            img.src = product.thumbnailPath;
            img.alt = '';
            span.appendChild(img);
        }
        span.appendChild(document.createTextNode(product.name));
        return span;
    }, []);

    /** Rebuild the whole box from plain text — only for values set from outside. */
    const writeValue = useCallback((text: string) => {
        const root = editorRef.current;
        if (!root) return;
        root.innerHTML = '';
        if (!text) return;
        // Longest first, so a name that begins another cannot shadow it.
        const names = productsRef.current
            .filter((p) => p.name)
            .sort((a, b) => b.name.length - a.name.length);
        let rest = text;
        while (rest) {
            const hit = names
                .map((p) => ({ p, i: rest.indexOf(`@${p.name}`) }))
                .filter((x) => x.i >= 0)
                .sort((a, b) => a.i - b.i)[0];
            if (!hit) { root.appendChild(document.createTextNode(rest)); break; }
            if (hit.i > 0) root.appendChild(document.createTextNode(rest.slice(0, hit.i)));
            root.appendChild(badgeFor(hit.p));
            rest = rest.slice(hit.i + hit.p.name.length + 1);
        }
    }, [badgeFor]);

    // Apply a value that came from outside (a handoff, or clearing after send).
    // Never while it merely echoes what we just reported, or the caret would
    // jump to the end on every keystroke.
    useEffect(() => {
        if (value === emitted.current) return;
        emitted.current = value;
        writeValue(value);
    }, [value, writeValue]);

    const emit = useCallback(() => {
        const root = editorRef.current;
        if (!root) return;
        const text = readEditor(root);
        emitted.current = text;
        onChange(text, textBeforeCaret(root));
    }, [onChange]);

    useImperativeHandle(ref, () => ({
        focus: () => editorRef.current?.focus(),
        insertMention: (product) => {
            const root = editorRef.current;
            if (!root) return;

            /**
             * Where the "@query" is, without trusting the caret.
             *
             * Clicking the picker blurs the editor, and a blurred
             * contenteditable has no usable selection — so an insertion that
             * only ever looked at `window.getSelection()` did nothing at all on
             * the click path, and left the "@" sitting there. The caret is used
             * when it really is inside this box; otherwise the last text node
             * still holding an "@" is where the vendor was typing.
             */
            let node: Text | null = null;
            let offset = 0;
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const r = sel.getRangeAt(0);
                if (root.contains(r.endContainer) && r.endContainer.nodeType === Node.TEXT_NODE) {
                    node = r.endContainer as Text;
                    offset = r.endOffset;
                }
            }
            if (!node) {
                const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
                let last: Text | null = null;
                while (walker.nextNode()) {
                    const t = walker.currentNode as Text;
                    if ((t.nodeValue || '').includes('@')) last = t;
                }
                if (!last) return;
                node = last;
                offset = (node.nodeValue || '').length;
            }

            const text = node.nodeValue || '';
            const at = text.lastIndexOf('@', Math.max(0, offset - 1));
            if (at < 0) return;

            const after = text.slice(offset);
            node.nodeValue = text.slice(0, at);

            const parent = node.parentNode;
            if (!parent) return;
            const badge = badgeFor(product);
            const spacer = document.createTextNode(' ');
            parent.insertBefore(badge, node.nextSibling);
            parent.insertBefore(spacer, badge.nextSibling);
            if (after) parent.insertBefore(document.createTextNode(after), spacer.nextSibling);

            // Put the caret after the badge and its space, so the sentence can
            // simply be continued — and focus first, or there is nothing to put
            // a caret into when this came from a click.
            root.focus();
            const put = document.createRange();
            put.setStart(spacer, 1);
            put.collapse(true);
            const s2 = window.getSelection();
            s2?.removeAllRanges();
            s2?.addRange(put);

            emit();
        },
    }), [badgeFor, emit]);

    return (
        <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            data-placeholder={placeholder || ''}
            onInput={emit}
            onKeyDown={(e: React.KeyboardEvent) => {
                // The panel owns the mention picker's keys and its own send
                // rules; a truthy result means it handled the key.
                if (onKeyDown?.(e)) return;
                /**
                 * Backspace takes a badge whole, explicitly.
                 *
                 * Browsers mostly do this for a `contenteditable="false"`
                 * element, but "mostly" is not a guarantee, and half-deleting a
                 * badge would leave a fragment that no longer names a product
                 * while still looking like one.
                 */
                if (e.key === 'Backspace' && !e.shiftKey) {
                    const sel = window.getSelection();
                    if (sel && sel.isCollapsed && sel.rangeCount > 0) {
                        const r = sel.getRangeAt(0);
                        let before: Node | null = null;
                        if (r.endContainer.nodeType === Node.TEXT_NODE && r.endOffset === 0) {
                            before = r.endContainer.previousSibling;
                        } else if (r.endContainer.nodeType === Node.ELEMENT_NODE) {
                            before = r.endContainer.childNodes[r.endOffset - 1] || null;
                        }
                        const el = before as HTMLElement | null;
                        if (el && el.nodeType === Node.ELEMENT_NODE && el.dataset?.mention) {
                            e.preventDefault();
                            el.remove();
                            emit();
                            return;
                        }
                    }
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit();
                }
            }}
            onPaste={(e: React.ClipboardEvent) => {
                // Plain text only: pasted markup would arrive as elements this
                // editor has no meaning for.
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain');
                document.execCommand('insertText', false, text);
            }}
            className={cn(
                'max-h-40 min-h-14 overflow-y-auto px-3 pt-3 pb-1',
                'text-sm leading-relaxed break-words whitespace-pre-wrap text-foreground outline-none',
                // The placeholder is drawn rather than owned by an <input>,
                // because a contenteditable has none of its own.
                'empty:before:pointer-events-none empty:before:text-muted-foreground',
                'empty:before:content-[attr(data-placeholder)]',
                // The badges are built imperatively by `badgeFor`, so they are
                // styled from here by class rather than by props.
                '[&_.cs-mention]:mx-0.5 [&_.cs-mention]:inline-flex [&_.cs-mention]:items-center',
                '[&_.cs-mention]:gap-[5px] [&_.cs-mention]:border [&_.cs-mention]:bg-muted',
                '[&_.cs-mention]:px-[7px] [&_.cs-mention]:py-px [&_.cs-mention]:align-baseline',
                '[&_.cs-mention]:text-[0.94em] [&_.cs-mention]:leading-normal [&_.cs-mention]:font-medium',
                '[&_.cs-mention]:whitespace-nowrap [&_.cs-mention]:select-none',
                '[&_.cs-mention_img]:block [&_.cs-mention_img]:size-[1.15em] [&_.cs-mention_img]:rounded-full',
                '[&_.cs-mention_img]:bg-muted [&_.cs-mention_img]:object-cover',
                disabled ? 'cursor-default opacity-60' : 'cursor-text',
            )}
        />
    );
});

MentionComposer.displayName = 'MentionComposer';
